import { useAtom } from "jotai";
import { SCHEMA_NAMES, type SchemaNames } from "instantdb/types";
import type { ResourceValues } from "@/lib/types";
import { getResourceInfo } from "@/utilities/getResourceInfo";
import { useNotification } from "@/hooks/useNotification";
import type { Goal, HealthArea, Routine } from "instantdb/types";
import { deleteGoal, upsertGoal } from "instantdb/client/goal";
import { upsertRoutine, deleteRoutine } from "instantdb/client/routine";
import { useUser } from "@/hooks/useUser";
import { deleteHealthArea, upsertHealthAreaGoalsAndRoutines } from "instantdb/client/healthArea";
import { useTranslation } from "@/hooks/useTranslation";

type useResourceMutationsReturn = {
  handleCreate: (values: ResourceValues) => Promise<void>;
  handleUpdate: (values: ResourceValues) => Promise<void>;
  handleDelete: (resourceId: string, resourceTitle: string) => Promise<void>;
};

/**
 * Properly format date fields for database storage
 * Converts Date objects to ISO strings
 */
const formatDatesForStorage = <T extends Record<string, unknown>>(resource: T): T => {
  if (!resource) return resource;

  const result = { ...resource };

  // Convert Date objects to ISO strings
  // @ts-expect-error - Dynamic property access for date conversion
  if (result.targetDate instanceof Date) {
    // @ts-expect-error - Dynamic property access for date conversion
    result.targetDate = result.targetDate.toISOString();
  }

  return result;
};

/**
 * Hook for managing resource mutations (create/update/delete)
 */
export const useResourceMutations = (slug: SchemaNames): useResourceMutationsReturn => {
  const { showNotification } = useNotification();
  const { labels, atom } = getResourceInfo(slug);
  const [managedAtom, setManagedAtom] = useAtom(atom);
  const { userId } = useUser();
  const { t } = useTranslation();

  const handleCreate = async (values: ResourceValues) => {
    try {
      const healthAreaId =
        typeof managedAtom.healthAreaId === "string" ? managedAtom.healthAreaId : undefined;

      const resource = {
        ...values,
        updatedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
      };

      // Format dates for database storage
      const formattedResource = formatDatesForStorage(resource);

      if (slug === SCHEMA_NAMES.healthAreas) {
        await upsertHealthAreaGoalsAndRoutines({
          ...formattedResource,
          userId,
        } as unknown as HealthArea);
      } else if (slug === SCHEMA_NAMES.goals) {
        const goalData = formattedResource;
        const goalPayload = healthAreaId
          ? { ...goalData, healthArea: { id: healthAreaId } }
          : goalData;
        await upsertGoal({ ...goalPayload, userId } as unknown as Goal);
      } else if (slug === SCHEMA_NAMES.routines) {
        const routineData = formattedResource;
        const routinePayload = healthAreaId
          ? { ...routineData, healthArea: { id: healthAreaId } }
          : routineData;
        await upsertRoutine({ ...routinePayload, userId } as unknown as Routine);
      }

      setManagedAtom({
        openSheet: false,
        formData: undefined,
        resource: undefined,
      });
    } catch (error) {
      showNotification({
        type: "error",
        message: "Error",
        description: t("hooks.error.failed_add_resource", { resource: labels.singular }),
      });
    }
  };

  const handleUpdate = async (values: ResourceValues) => {
    const resourceId = managedAtom.resource?.id;
    if (!resourceId) {
      showNotification({
        type: "error",
        message: "Error",
        description: t("hooks.error.no_resource_id"),
      });
      return;
    }

    try {
      const updatedValues = {
        ...values,
        id: resourceId,
        updatedAt: new Date().toISOString(),
      };

      // Format dates for database storage
      const formattedValues = formatDatesForStorage(updatedValues);

      if (slug === SCHEMA_NAMES.healthAreas) {
        await upsertHealthAreaGoalsAndRoutines({
          ...formattedValues,
          userId,
        } as unknown as HealthArea);
      } else if (slug === SCHEMA_NAMES.goals) {
        await upsertGoal({ ...formattedValues, userId } as unknown as Goal);
      } else if (slug === SCHEMA_NAMES.routines) {
        await upsertRoutine({ ...formattedValues, userId } as unknown as Routine);
      }

      setManagedAtom({
        openSheet: false,
        formData: undefined,
        resource: undefined,
      });
    } catch (error) {
      showNotification({
        type: "error",
        message: "Error",
        description: t("hooks.error.failed_update_resource", {
          resource: labels.singular,
        }),
      });
    }
  };

  const handleDelete = async (resourceId: string, resourceTitle: string) => {
    try {
      if (slug === SCHEMA_NAMES.healthAreas) {
        await deleteHealthArea(resourceId);
      } else if (slug === SCHEMA_NAMES.goals) {
        await deleteGoal(resourceId);
      } else if (slug === SCHEMA_NAMES.routines) {
        await deleteRoutine(resourceId);
      }

      const title = slug === SCHEMA_NAMES.healthAreas ? t(resourceTitle) : resourceTitle;

      showNotification({
        type: "success",
        message: "Success",
        description: t("hooks.success.deleted_resource", { title }),
      });
    } catch (error) {
      showNotification({
        type: "error",
        message: "Error",
        description: t("hooks.error.failed_delete_resource", {
          resource: labels.singular,
        }),
      });
    }
  };

  return {
    handleCreate,
    handleUpdate,
    handleDelete,
  };
};
